import {create} from 'zustand';
import {Pagination} from '../../../Data/types/BaseResponse';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {ChecklistsFactory} from './checklistsFactory';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {Checklist, ChecklistFilters, ChecklistPayload, ChecklistStatus} from '../models/checklist.model';

interface ChecklistsState {
  checklists: Checklist[];
  pagination: Pagination | undefined;
  filters: ChecklistFilters;
  loading: boolean;
  error: string | null;

  fetchChecklists: (page?: number, limit?: number, filters?: ChecklistFilters) => Promise<void>;
  getChecklist: (id: number) => Promise<Checklist>;
  createChecklist: (payload: ChecklistPayload) => Promise<Checklist>;
  updateChecklist: (id: number, payload: ChecklistPayload) => Promise<Checklist>;
  setChecklistStatus: (id: number, status: ChecklistStatus) => Promise<Checklist>;
  deleteChecklist: (id: number) => Promise<void>;
  refreshData: () => Promise<void>;
  clearErrors: () => void;
}

export const useChecklistsStore = create<ChecklistsState>((set, get) => {
  // Initialize use cases using factory
  const listChecklistsUseCase = ChecklistsFactory.createListChecklistsUseCase();
  const getChecklistByIdUseCase = ChecklistsFactory.createGetChecklistByIdUseCase();
  const createChecklistUseCase = ChecklistsFactory.createCreateChecklistUseCase();
  const updateChecklistUseCase = ChecklistsFactory.createUpdateChecklistUseCase();
  const setChecklistStatusUseCase = ChecklistsFactory.createSetChecklistStatusUseCase();
  const deleteChecklistUseCase = ChecklistsFactory.createDeleteChecklistUseCase();
  const errorHandler = StandardizedErrorHandler.getInstance();

  return {
    checklists: [],
    pagination: undefined,
    filters: {},
    loading: false,
    error: null,

    fetchChecklists: async (page = PAGINATION_CONFIG.DEFAULT_PAGE, limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE, filters) => {
      const activeFilters = filters !== undefined ? filters : get().filters;
      set({loading: true, error: null, filters: activeFilters});
      try {
        const {items, pagination} = await listChecklistsUseCase.execute(page, limit, activeFilters);
        set({checklists: items || [], pagination, loading: false});
      } catch (err) {
        const appError = errorHandler.toAppError(err);
        set({error: appError.message, loading: false});
        errorHandler.handleError(err); // For notifications/logging
      }
    },

    getChecklist: async (id: number) => {
      return getChecklistByIdUseCase.execute(id);
    },

    createChecklist: async (payload: ChecklistPayload) => {
      try {
        const created = await createChecklistUseCase.execute(payload);
        await get().refreshData();
        return created;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    updateChecklist: async (id: number, payload: ChecklistPayload) => {
      try {
        const updated = await updateChecklistUseCase.execute(id, payload);
        await get().refreshData();
        return updated;
      } catch (err) {
        throw err;
      }
    },

    setChecklistStatus: async (id: number, status: ChecklistStatus) => {
      try {
        const updated = await setChecklistStatusUseCase.execute(id, status);
        await get().refreshData();
        return updated;
      } catch (err) {
        throw err;
      }
    },

    deleteChecklist: async (id: number) => {
      try {
        await deleteChecklistUseCase.execute(id);
        await get().refreshData();
      } catch (err) {
        throw err;
      }
    },

    refreshData: async () => {
      const {pagination} = get();
      const page = pagination?.page || PAGINATION_CONFIG.DEFAULT_PAGE;
      const limit = pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
      try {
        await get().fetchChecklists(page, limit);
      } catch {
        // refresh failures are non-critical; the primary op already succeeded
      }
    },

    clearErrors: () => set({error: null})
  };
});
